import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Trophy, Globe2, ShieldCheck, BadgeCheck } from "lucide-react";
import { CATEGORIES, PRODUCTS, BRAND_TILES, byCategory } from "@/data/catalog";
import { HeroCarousel } from "@/components/HeroCarousel";
import { ProductCard } from "@/components/ProductCard";

const Section = ({ eyebrow, title, sub, link, children, testId }) => {
  const { t } = useTranslation();
  return (
    <section data-testid={testId} className="nv-container mt-24">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          {eyebrow && <p className="nv-eyebrow">{eyebrow}</p>}
          <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">{title}</h2>
          {sub && <p className="mt-2 max-w-xl text-base text-black/60">{sub}</p>}
        </div>
        {link && <Link to={link} className="nv-btn-ghost !px-5 !py-2.5 text-xs">{t("nav.viewAll")} <ArrowRight size={14} /></Link>}
      </div>
      <div className="mt-8">{children}</div>
    </section>
  );
};

export default function EShop() {
  const { t } = useTranslation();
  const loved = PRODUCTS.filter((p) => p.tag === "loved").slice(0, 8);
  const news = PRODUCTS.filter((p) => p.tag === "new").concat(PRODUCTS.filter((p) => p.tag === "promo")).slice(0, 4);
  const pillars = [[Trophy, "p1"], [Globe2, "p2"], [ShieldCheck, "p3"], [BadgeCheck, "p4"]];

  return (
    <main className="pb-24">
      <HeroCarousel />

      <div className="h-px w-full bg-[#c6ff00]" />

      <Section testId="home-categories" eyebrow="NINJAVITY" title={t("home.explore")} sub={t("home.exploreSub")}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {CATEGORIES.map((c, i) => (
            <Link key={c.id} to={`/c/${c.id}`} data-testid={`home-cat-${c.id}`} className="group nv-fade-up relative overflow-hidden rounded-3xl bg-black text-white" style={{ animationDelay: `${i * 80}ms`, minHeight: 420 }}>
              <img src={c.thumb} alt="" className="nv-card-img absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/5" />
              <div className="relative flex h-full flex-col justify-end p-6">
                <span className="text-xs font-bold uppercase tracking-wider text-[#c6ff00]">{byCategory(c.id).length} {t("home.products")}</span>
                <h3 className="mt-1 font-display text-3xl font-bold">{t(`nav.${c.id}`)}</h3>
                <p className="mt-2 line-clamp-2 text-sm text-white/70">{t(`catDesc.${c.id}`)}</p>
                <span className="mt-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#c6ff00] text-black transition-transform group-hover:translate-x-2"><ArrowRight size={16} /></span>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <Section testId="home-bestsellers" eyebrow="NINJAVITY" title={t("home.bestsellers")} sub={t("home.bestsellersSub")}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{loved.map((p, i) => <ProductCard key={p.code} p={p} index={i} />)}</div>
      </Section>

      <section className="nv-container mt-24">
        <div className="nv-grain relative overflow-hidden rounded-3xl bg-black px-8 py-14 text-white md:px-14">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#c6ff00]/25 blur-3xl" />
          <div className="relative grid gap-10 lg:grid-cols-2">
            <div>
              <p className="nv-eyebrow !text-[#c6ff00]">NINJAVITY</p>
              <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl lg:text-5xl">{t("home.tagline")}</h2>
              <Link to="/c/bellezza" className="nv-btn-lime mt-8">{t("nav.bellezza")} <ArrowRight size={16} /></Link>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-2">
              {news.map((p) => (
                <Link key={p.code} to={`/p/${p.code}`} className="group rounded-2xl bg-white/5 p-3 ring-1 ring-white/10 transition-colors hover:bg-[#c6ff00] hover:text-black">
                  <div className="aspect-square overflow-hidden rounded-xl bg-white"><img src={p.img} alt={p.name} className="nv-card-img h-full w-full object-contain p-2" /></div>
                  <p className="mt-2 line-clamp-2 text-xs font-semibold">{p.name}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Section testId="home-brands" eyebrow="NINJAVITY" title={t("home.brands")} sub={t("home.brandsSub")}>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {BRAND_TILES.map((b, i) => (
            <Link key={b.name} to={`/c/${b.cat}?brand=${encodeURIComponent(b.name)}`} data-testid={`brand-tile-${i}`} className="group overflow-hidden rounded-2xl border border-black/[0.06] bg-white transition-[transform,box-shadow] hover:-translate-y-1 hover:shadow-lg">
              <div className="aspect-[3/2] overflow-hidden"><img src={b.img} alt={b.name} className="nv-card-img h-full w-full object-cover" /></div>
              <p className="p-3 text-center font-display text-sm font-bold">{b.name}</p>
            </Link>
          ))}
        </div>
      </Section>

      <Section testId="home-pillars" eyebrow="NINJAVITY" title={t("home.pillars")}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {pillars.map(([Icon, k]) => (
            <div key={k} className="rounded-2xl bg-[#f4f5f0] p-6 transition-colors hover:bg-[#c6ff00]">
              <Icon size={28} />
              <h3 className="mt-4 font-display text-lg font-bold">{t(`home.${k}`)}</h3>
              <p className="mt-1 text-sm text-black/60">{t(`home.${k}s`)}</p>
            </div>
          ))}
        </div>
      </Section>

      <section className="nv-container mt-24">
        <div className="flex flex-col items-start justify-between gap-6 rounded-3xl border border-black/10 p-10 md:flex-row md:items-center">
          <div>
            <h2 className="font-display text-3xl font-bold sm:text-4xl">{t("home.ctaTitle")}</h2>
            <p className="mt-2 max-w-xl text-black/60">{t("home.ctaSub")}</p>
          </div>
          <Link to="/register" data-testid="home-cta-register" className="nv-btn-dark">{t("home.ctaBtn")} <ArrowRight size={16} /></Link>
        </div>
      </section>
    </main>
  );
}
